import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  CreditCard, 
  Plus, 
  Building, 
  Trash2,
  Loader2,
  Check
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

export default function PaymentMethodsPage({ user }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBankDialog, setShowAddBankDialog] = useState(false);
  const [showAddCardDialog, setShowAddCardDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Bank form data
  const [bankFormData, setBankFormData] = useState({
    name: "",
    bank_name: "",
    account_type: "checking",
    routing_number: "",
    account_number: "",
    account_holder_name: ""
  });
  
  // Card form data
  const [cardFormData, setCardFormData] = useState({
    name: "",
    card_number: "",
    expiry_month: "",
    expiry_year: "",
    cardholder_name: "",
    billing_zip: ""
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/payment-methods`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data);
      }
    } catch (err) {
      console.error("Error fetching payment methods:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBank = async (e) => {
    e.preventDefault();
    if (!bankFormData.account_holder_name || !bankFormData.routing_number || !bankFormData.account_number) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (bankFormData.routing_number.length !== 9) {
      toast.error("Routing number must be 9 digits");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/payment-methods`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          type: "bank",
          name: bankFormData.name || `${bankFormData.bank_name} ${bankFormData.account_type}`,
          ...bankFormData
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to add bank account");
      }
      
      toast.success("Bank account added successfully!");
      setShowAddBankDialog(false);
      setBankFormData({ name: "", bank_name: "", account_type: "checking", routing_number: "", account_number: "", account_holder_name: "" });
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCard = async (e) => {
    e.preventDefault();
    if (!cardFormData.cardholder_name || !cardFormData.card_number || !cardFormData.expiry_month || !cardFormData.expiry_year) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Basic card number validation
    const cleanCardNumber = cardFormData.card_number.replace(/\s/g, "");
    if (cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
      toast.error("Please enter a valid card number");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/payment-methods`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          type: "card",
          name: cardFormData.name || `Card ending in ${cleanCardNumber.slice(-4)}`,
          card_number: cleanCardNumber,
          expiry_month: cardFormData.expiry_month,
          expiry_year: cardFormData.expiry_year,
          cardholder_name: cardFormData.cardholder_name,
          billing_zip: cardFormData.billing_zip
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to add card");
      }
      
      toast.success("Card added successfully!");
      setShowAddCardDialog(false);
      setCardFormData({ name: "", card_number: "", expiry_month: "", expiry_year: "", cardholder_name: "", billing_zip: "" });
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/payment-methods/${methodId}/default`, {
        method: "PUT",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to set default");
      }
      
      toast.success("Default payment method updated");
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    if (!selectedMethod) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/payment-methods/${selectedMethod.method_id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete payment method");
      }
      
      toast.success("Payment method deleted");
      setShowDeleteDialog(false);
      setSelectedMethod(null);
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(" ") : value;
  };

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Payment Methods</h1>
          <p className="text-gray-500 mt-1">Manage your connected bank accounts and cards</p>
        </div>

        {/* Add Payment Method */}
        <div className="flex gap-3">
          <Button 
            className="bg-[#2563eb] hover:bg-[#1d4ed8] gap-2" 
            onClick={() => setShowAddBankDialog(true)}
            data-testid="add-bank-btn"
          >
            <Building className="w-4 h-4" />
            Add Bank Account
          </Button>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setShowAddCardDialog(true)}
            data-testid="add-card-btn"
          >
            <CreditCard className="w-4 h-4" />
            Add Card
          </Button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : paymentMethods.length === 0 ? (
          <Card className="border border-gray-200">
            <CardContent className="py-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No payment methods yet</h3>
                <p className="text-gray-500 mb-4">Add a bank account or card to get started</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <Card key={method.method_id} className="border border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        {method.type === "bank" ? (
                          <Building className="w-6 h-6 text-gray-500" />
                        ) : (
                          <CreditCard className="w-6 h-6 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{method.name}</p>
                          {method.default && (
                            <Badge className="bg-green-100 text-green-700">Default</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {method.type === "bank" ? (
                            <>
                              {method.bank_name && `${method.bank_name} • `}
                              {method.account_type?.charAt(0).toUpperCase() + method.account_type?.slice(1)} • 
                              {method.account_number_masked || `****${method.last4}`}
                            </>
                          ) : (
                            <>
                              {method.card_number_masked || `****-****-****-${method.last4}`}
                              {method.expiry_month && ` • Exp ${method.expiry_month}/${method.expiry_year}`}
                            </>
                          )}
                        </p>
                        {method.account_holder_name && (
                          <p className="text-xs text-gray-400">{method.account_holder_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!method.default && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSetDefault(method.method_id)}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          setSelectedMethod(method);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddBankDialog} onOpenChange={setShowAddBankDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBank} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  placeholder="e.g., Chase, Bank of America"
                  value={bankFormData.bank_name}
                  onChange={(e) => setBankFormData({ ...bankFormData, bank_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Account Type</Label>
                <Select
                  value={bankFormData.account_type}
                  onValueChange={(value) => setBankFormData({ ...bankFormData, account_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Account Holder Name *</Label>
              <Input
                placeholder="Full name on account"
                value={bankFormData.account_holder_name}
                onChange={(e) => setBankFormData({ ...bankFormData, account_holder_name: e.target.value })}
                data-testid="bank-holder-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Routing Number *</Label>
                <Input
                  placeholder="9-digit routing number"
                  maxLength={9}
                  value={bankFormData.routing_number}
                  onChange={(e) => setBankFormData({ ...bankFormData, routing_number: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                  data-testid="bank-routing-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input
                  placeholder="Account number"
                  value={bankFormData.account_number}
                  onChange={(e) => setBankFormData({ ...bankFormData, account_number: e.target.value.replace(/\D/g, "") })}
                  data-testid="bank-account-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nickname (optional)</Label>
              <Input
                placeholder="e.g., Business Checking"
                value={bankFormData.name}
                onChange={(e) => setBankFormData({ ...bankFormData, name: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddBankDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                disabled={submitting}
                data-testid="save-bank-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Bank Account"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Card Dialog */}
      <Dialog open={showAddCardDialog} onOpenChange={setShowAddCardDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Card</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCard} className="space-y-4">
            <div className="space-y-2">
              <Label>Cardholder Name *</Label>
              <Input
                placeholder="Name on card"
                value={cardFormData.cardholder_name}
                onChange={(e) => setCardFormData({ ...cardFormData, cardholder_name: e.target.value })}
                data-testid="card-holder-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Card Number *</Label>
              <Input
                placeholder="1234 5678 9012 3456"
                value={cardFormData.card_number}
                onChange={(e) => setCardFormData({ ...cardFormData, card_number: formatCardNumber(e.target.value) })}
                maxLength={19}
                data-testid="card-number-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Expiry Month *</Label>
                <Select
                  value={cardFormData.expiry_month}
                  onValueChange={(value) => setCardFormData({ ...cardFormData, expiry_month: value })}
                >
                  <SelectTrigger data-testid="card-month-select">
                    <SelectValue placeholder="MM" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                        {String(i + 1).padStart(2, "0")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Expiry Year *</Label>
                <Select
                  value={cardFormData.expiry_year}
                  onValueChange={(value) => setCardFormData({ ...cardFormData, expiry_year: value })}
                >
                  <SelectTrigger data-testid="card-year-select">
                    <SelectValue placeholder="YY" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <SelectItem key={year} value={String(year).slice(-2)}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Billing ZIP</Label>
                <Input
                  placeholder="12345"
                  maxLength={5}
                  value={cardFormData.billing_zip}
                  onChange={(e) => setCardFormData({ ...cardFormData, billing_zip: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nickname (optional)</Label>
              <Input
                placeholder="e.g., Business Visa"
                value={cardFormData.name}
                onChange={(e) => setCardFormData({ ...cardFormData, name: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddCardDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                disabled={submitting}
                data-testid="save-card-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Card"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedMethod?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
